import { useEffect, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import {
  Card, Select, SelectItem, SearchSelect, SearchSelectItem, Text, Title,
} from '@tremor/react';
import { currentUser } from '../api/auth';
import { fetchOverview } from '../api/client';
import { fetchAR } from '../api/drilldown';
import AccessDenied from '../components/AccessDenied';
import AccountStatement from '../components/AccountStatement';
import EmptyState from '../components/EmptyState';
import Header from '../components/Header';
import PaymentApplyPanel from '../components/PaymentApplyPanel';
import { canApplyPayments } from '../lib/roles';
import { t } from '../lib/i18n';
import type { ARCustomer, OverviewPayload, User } from '../api/types';

/**
 * Aplicar pago — a standalone entry point (Clientes menu) for posting a
 * customer payment, so cashiers don't have to find the account in the CxC
 * aging list first. Same building blocks CxC uses inline: pick store +
 * account (deep-linkable as ?sid&anum, like Estado de cuenta), then the
 * PaymentApplyPanel with the account statement underneath for context.
 * Gate = canApplyPayments (writes on + owner/accounting), the exact rule the
 * CxC panel applies; the server enforces it regardless.
 */
export default function Pagos() {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  useEffect(() => {
    let alive = true;
    currentUser()
      .then((u) => { if (alive) setUser(u); })
      .catch(() => { if (alive) setUser(null); });
    return () => { alive = false; };
  }, []);
  if (user === undefined) return null;
  return user ? <PagosInner user={user} /> : <Navigate to="/login" replace />;
}

function PagosInner({ user }: { user: User }) {
  const authorized = canApplyPayments(user);
  const [params, setParams] = useSearchParams();

  const [overview, setOverview] = useState<OverviewPayload | null>(null);
  const [accounts, setAccounts] = useState<ARCustomer[]>([]);

  // Selection, seeded from the URL so CxC / Customer deep-links land pre-filled.
  const qSid = Number(params.get('sid'));
  const qAnum = Number(params.get('anum'));
  const [sid, setSid] = useState<number>(qSid > 0 ? qSid : (user.sid_assigned ?? 0));
  const [anum, setAnum] = useState<number>(qAnum > 0 ? qAnum : 0);

  useEffect(() => {
    if (!authorized) return;
    let alive = true;
    fetchOverview(user.role)
      .then((p) => {
        if (!alive) return;
        setOverview(p);
        // Default the store to the first one if nothing chosen yet.
        if (!(qSid > 0) && user.sid_assigned == null && p.stores.length > 0) {
          setSid((cur) => (cur > 0 ? cur : p.stores[0].sid));
        }
      })
      .catch(() => { /* store select just hides */ });
    fetchAR({ limit: 2000, sort: 'name' })
      .then((rows) => { if (alive) setAccounts(rows); })
      .catch(() => { /* picker stays empty; deep-link still works */ });
    return () => { alive = false; };
  }, [authorized, user.role, user.sid_assigned, qSid]);

  // Keep the URL in sync so the view is shareable / refresh-safe.
  useEffect(() => {
    if (sid > 0 && anum > 0) {
      setParams({ sid: String(sid), anum: String(anum) }, { replace: true });
    }
  }, [sid, anum]); // eslint-disable-line react-hooks/exhaustive-deps

  const account = accounts.find((a) => a.anum === anum);

  return (
    <div className="min-h-screen flex flex-col">
      <Header user={user} />
      <main className="flex-1 px-6 py-6 space-y-4 max-w-screen-2xl mx-auto w-full">
        <div>
          <Title>{t.pagos.title}</Title>
          <Text className="text-tremor-content text-sm">{t.pagos.subtitle}</Text>
        </div>

        {!authorized ? (
          <AccessDenied user={user} />
        ) : (
          <>
            <Card className="flex flex-col md:flex-row gap-3">
              {overview && overview.stores.length > 1 && (
                <div className="w-full md:w-48">
                  <div className="text-tremor-content text-xs mb-1">{t.statement.store}</div>
                  <Select value={sid > 0 ? String(sid) : ''} onValueChange={(v) => setSid(Number(v))} enableClear={false}>
                    {overview.stores.map((s) => (
                      <SelectItem key={s.sid} value={String(s.sid)}>{s.name}</SelectItem>
                    ))}
                  </Select>
                </div>
              )}
              <div className="w-full md:w-96">
                <div className="text-tremor-content text-xs mb-1">{t.statement.account}</div>
                <SearchSelect value={anum > 0 ? String(anum) : ''} onValueChange={(v) => setAnum(Number(v))} placeholder={t.statement.accountPlaceholder}>
                  {accounts.map((a) => (
                    <SearchSelectItem key={a.anum} value={String(a.anum)}>
                      {a.anum} · {a.name}
                    </SearchSelectItem>
                  ))}
                </SearchSelect>
              </div>
            </Card>

            {sid > 0 && anum > 0 ? (
              <>
                {account && (
                  <Text className="text-sm text-tremor-content-strong">
                    {account.anum} · {account.name}
                  </Text>
                )}
                <PaymentApplyPanel sid={sid} anum={anum} user={user} />
                <AccountStatement sid={sid} anum={anum} />
              </>
            ) : (
              <EmptyState message={t.pagos.pickPrompt} />
            )}
          </>
        )}
      </main>
    </div>
  );
}
